import { useOwnedAccount } from '@hotjar/account';
import { useCurrentSite } from '@hotjar/site';
import { useMemo, useCallback } from 'react';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { trackClick } from '@hotjar/tracking';
import { useSurveyQuestions } from '../../../../hooks/survey/useSurveyQuestions';
import type { SelectedQuestion, SelectedQuestions } from '../hooks/useSelectedQuestionStore';
import { useSelectedQuestionStore } from '../hooks/useSelectedQuestionStore';
import type { SurveyQuestion } from '../../../../domain/Surveys/Questions';

function getSelectedQuestion(
  selectedQuestions: SelectedQuestions,
  siteId: SiteId | undefined,
  surveyId: number | undefined,
): SelectedQuestion | undefined {
  if (siteId === undefined || surveyId === undefined) return undefined;
  const widgets = selectedQuestions[siteId];
  if (widgets === undefined) return undefined;
  return widgets[surveyId];
}

export const useSelectedQuestion = (
  surveyId: number | undefined,
  allowAll: boolean,
): 'ALL' | SurveyQuestion | undefined => {
  const { data: site } = useCurrentSite();
  const { questions } = useSurveyQuestions(surveyId);
  const { selectedQuestions } = useSelectedQuestionStore();

  const result = useMemo(() => {
    const selectedQuestion = getSelectedQuestion(selectedQuestions, site?.id, surveyId);
    if (questions === undefined || questions.length === 0) {
      return undefined;
    }

    if (selectedQuestion !== 'ALL') {
      const question = questions.find((q) => q.uuid === selectedQuestion);
      if (question !== undefined) {
        return question;
      }
    }

    return allowAll ? 'ALL' : questions[0];
  }, [selectedQuestions, site?.id, surveyId, questions, allowAll]);

  if (questions === undefined) return undefined;

  return result;
};

export const useSetSelectedQuestion = (
  surveyId: number,
  trackingEventName: string,
): ((selectedQuestion: SelectedQuestion) => void) => {
  const { data: site } = useCurrentSite();
  const { questions } = useSurveyQuestions(surveyId);
  const { setSelectedQuestion: setSelectedQuestionStore } = useSelectedQuestionStore();
  const { data: account } = useOwnedAccount();

  const setSelectedQuestion = useCallback(
    (selectedQuestion: SelectedQuestion) => {
      if (selectedQuestion === undefined) throw new Error('Question not found');
      if (site === undefined) throw new Error('Site not loaded');

      setSelectedQuestionStore(site.id, surveyId, selectedQuestion);

      if (account === undefined || questions === undefined) return;

      trackClick({
        name: trackingEventName,
        uiPosition:
          selectedQuestion === 'ALL'
            ? 'all questions'
            : `question ${questions.findIndex((q) => q.uuid === selectedQuestion) + 1}`,
        groupData: extractAccountGroupingProps(account),
      });
    },
    [site, setSelectedQuestionStore, surveyId, account, questions, trackingEventName],
  );

  return setSelectedQuestion;
};
